import { Anchor } from "@/app/components/anchor"
import ResponsiveIframeEmbed from "@/components/tools/ResponsiveIframeEmbed"
import CostPerMileTrendChart from "@/components/cost/CostPerMileTrendChart"
import { buildCostSectionView } from "@/util/cost-section-view"
import { describeTrend, formatSnapshotMonth } from "@/util/describe-trend"
import type {
  CostSummaryBand,
  MarketplaceCostSummary,
} from "@/util/cost-summary"

type CalculatorEmbed = {
  iframeUrl: string
  title: string
  responsiveHeight?: {
    xs?: string
    sm?: string
    md?: string
    lg?: string
    xl?: string
  }
}

type PetCostSectionProps = {
  // "Los Angeles, CA" on city pages; omitted on state pages.
  cityLabel?: string
  stateLabel: string
  costBand: CostSummaryBand | null
  marketplaceCost: MarketplaceCostSummary | null
  costDataWindow: { from: string; to: string } | null
  calculator: CalculatorEmbed
  // tools.json id, used only for the <noscript> link (keeps tools.json out of
  // the client bundle — see .claude/rules/programmatic-pages.md).
  calculatorToolId: string
}

// Calculator embed + native cost-per-mile band + trend, shared by the pet city
// and state landing pages. Mirrors the boat city page's cost section; the band
// and trend render server-side so the cost copy is crawlable, not locked in an
// iframe. The band/trend selection logic lives in buildCostSectionView (pure,
// tested); this component only formats and lays it out.
export default function PetCostSection({
  cityLabel,
  stateLabel,
  costBand,
  marketplaceCost,
  costDataWindow,
  calculator,
  calculatorToolId,
}: PetCostSectionProps) {
  const locationLabel = cityLabel ?? stateLabel
  const { band, trend } = buildCostSectionView({
    cityLabel,
    stateLabel,
    costBand,
    marketplaceCost,
  })

  return (
    <section className="mt-10 md:mt-20" id="shipping-cost">
      <div className="max-w-[1289px] mx-auto px-4 md:px-8 mb-12">
        <div className="md:text-center w-full mb-8">
          <h2 className="font-semibold mb-3 font-montserrat text-[32px] leading-10 text-blue7">
            Pet Shipping Costs in {locationLabel}
          </h2>
          <p className="max-w-4xl mx-auto text-md text-gray-600">
            Review typical pet transportation cost ranges for {locationLabel},
            then use the calculator to estimate pricing for your pet and route
            before requesting quotes from transporters.
          </p>
        </div>
        <div className="space-y-12">
          {band && (
            <div className="max-w-3xl mx-auto">
              <h3 className="mb-3 text-center font-semibold font-montserrat text-xl md:text-2xl">
                Typical Pet Transport Cost Per Mile
              </h3>
              <p className="mb-5 text-center text-md text-gray-600">
                {band.scopeSentence}
                {costDataWindow
                  ? `, from bookings completed between ${formatSnapshotMonth(
                      costDataWindow.from,
                    )} and ${formatSnapshotMonth(costDataWindow.to)}.`
                  : "."}{" "}
                Your price depends on the animal, distance, and season. Use the
                calculator below for a route-specific estimate.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                {([
                  ["Low", band.percentiles.low],
                  ["Typical", band.percentiles.typical],
                  ["High", band.percentiles.high],
                  ["Peak", band.percentiles.peak],
                ] as const).map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-lg border border-gray-200 bg-blue16 px-3 py-4"
                  >
                    <div className="text-sm text-gray-600">{label}</div>
                    <div className="text-xl font-semibold font-montserrat">
                      ${value.toFixed(2)}
                      <span className="text-sm font-normal text-gray-600">
                        /mi
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {band.comparison && (
                <p className="mt-4 text-center text-sm text-gray-600">
                  {band.comparison}
                </p>
              )}
              {trend &&
                (() => {
                  const trendSummary = describeTrend(trend.data)
                  return (
                    <div className="mt-8">
                      <h4 className="mb-2 text-center font-semibold font-montserrat text-md md:text-lg">
                        {trend.heading}
                      </h4>
                      <p className="mb-4 text-center text-sm text-gray-600">
                        {trendSummary}
                      </p>
                      {/* This section is nested inside the page's px-4 wrapper, so on
                        mobile the chart/calculator would otherwise carry double the
                        horizontal padding of the boat page. Cancel one px-4 layer
                        on just these two so they get the same width as boats; the
                        headings and band cards keep the default spacing. */}
                      <div className="-mx-4 md:mx-0">
                        <CostPerMileTrendChart
                          trend={trend.data}
                          ariaLabel={trendSummary}
                        />
                      </div>
                    </div>
                  )
                })()}
            </div>
          )}
          <div>
            <h3 className="mb-3 text-center font-semibold font-montserrat text-xl md:text-2xl">
              Estimate Your Pet Shipping Cost
            </h3>
            <p className="max-w-3xl mx-auto mb-5 text-center text-md text-gray-600">
              Enter your pickup and delivery details to estimate the cost of
              shipping a pet to or from {locationLabel}. The calculator uses
              CitizenShipper marketplace transaction data and is free to use,
              with no account required.
            </p>
            {/* Widen on mobile to match boats — see the chart wrapper above. */}
            <div className="-mx-4 md:mx-0">
              <ResponsiveIframeEmbed
                src={calculator.iframeUrl}
                title={calculator.title}
                defaultHeight="770px"
                responsiveHeight={calculator.responsiveHeight}
                maxWidth="1000px"
                // The calculator reports its height on mount; its real minimum is
                // the form. Floor well under that so the tiny pre-render post
                // can't squish the frame below the responsive fallback before it
                // lays out.
                minDynamicHeight={300}
              />
            </div>
            <noscript>
              <p className="max-w-3xl mx-auto mt-4 text-center text-sm text-gray-600">
                The interactive pet shipping cost calculator requires
                JavaScript. Open the full{" "}
                <Anchor
                  href={`/tools/${calculatorToolId}`}
                  className="text-blue7 underline"
                >
                  Pet Shipping Cost Calculator
                </Anchor>{" "}
                to estimate your cost.
              </p>
            </noscript>
          </div>
        </div>
      </div>
    </section>
  )
}
